import React from "react";
import { getSnapshot, types } from "mobx-state-tree";
import { InspectionColumn, InspectionColumnGroup } from "../../components/inspection/InspectionGrid";
import { DetectionModel } from "../../models/DetectionModel";
import { translationService } from "../translation/TranslationService";
import { videoPlayerService } from "../video-player/VideoPlayerService";
import { ocrTsToSeconds } from "../../components/inspection/playbackSync";

// A detection's time as a clock reading, or "â€”" when the row carries no usable timestamp.
// Never let `new Date("")` reach the screen: it renders the literal string "Invalid Date" in
// every row, which reads as corrupted data rather than "this row has no time".
export const formatDetectionTime = (value: unknown): string => {
  if (typeof value !== "string" || value === "") return "â€”";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "â€”";
  return parsed.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export type ViewType = "detected" | "extracted" | "combined";

export const SelectedView = {
  Detected: "detected" as ViewType,
  Extracted: "extracted" as ViewType,
  Combined: "combined" as ViewType,
};

function renderClickablecell(displayValue: string | number, timestamp : string) {
  const seconds = ocrTsToSeconds(timestamp);
  return (
    <a
      href="#"
      style={{ cursor: "pointer", textDecoration: "underline" }}
      onClick={(event) => {
        event.preventDefault();
        videoPlayerService.jumpTo(seconds);
        const index = inspectionInfoStorage.activeDetections.findIndex(
          (detection: any) => detection.timestamp === timestamp
        );
        if (index !== -1) {
          inspectionInfoStorage.setSelectedIndex(index);
        }
      }}
    >
      {displayValue}
    </a>
  );
}

const InspectionInfoStorageModel = types.model("InspectionInfoStorageModel", {
  detections: types.optional(types.array(DetectionModel), []),
  view: types.optional(types.enumeration<ViewType>("ViewType", [SelectedView.Detected, SelectedView.Extracted, SelectedView.Combined]), SelectedView.Detected),
  selectedIndex: types.maybeNull(types.number),

  // The reviewer's approved detection list for this file, in the same shape as the OCR
  // output. Kept SEPARATE from `detections` on purpose: ocr.json has to stay exactly as the
  // machine produced it so the two can be compared and the original is never lost.
  approvedDetections: types.optional(types.frozen<any[]>(), []),

  // Which list the graph and the detection list are built from. The OCR output is the default
  // — it is what exists before a review — and the source selector switches to the approved
  // list, which is the reviewed report once detections have been approved.
  useApproved: types.optional(types.boolean, false),

}).views((self) => ({
  // The list actually on screen. This â€” not `detections` â€” is what every consumer should
  // read, so the source switch reaches the table, the pipe and the cards together.
  //
  // NO fallback to the OCR output: with nothing approved this is empty on purpose, so the
  // graph and the detection list stay blank until a reviewer has approved detections rather
  // than presenting raw machine output as an inspection.
  get activeDetections(): any[] {
    if (self.useApproved) return self.approvedDetections;
    return self.detections as any[];
  },
  get hasApprovedList(): boolean {
    return self.approvedDetections.length > 0;
  },
  /**
   * The ocr.json rows as a SNAPSHOT. This, not `detections` or `activeDetections`, is what a
   * React hook DEPENDENCY must be built from.
   *
   * `setDetections` fills the array with `array.replace()`, an IN-PLACE mutation, so the MST
   * array node keeps one identity for the whole life of this singleton. A `useMemo` keyed on the
   * array therefore computes exactly once (on its consumer's first render, while the store is
   * still empty) and never recomputes, leaving that consumer permanently holding an empty list.
   * (That is what silently broke "Approve all": the machine output it copies into the approved
   * list was always empty after a page refresh.) A computed over the snapshot changes identity
   * precisely when the rows change, and is cached in between, so it is safe to use as the
   * dependency of an effect as well.
   *
   * Exposed here rather than calling getSnapshot() in the app: the app and this library resolve
   * DIFFERENT mobx-state-tree copies, so MST helpers must be applied on the side that owns the
   * node.
   */
  get detectionRows(): any[] {
    return getSnapshot(self.detections) as any[];
  },
})).views((self) => ({
  // The reviewer's verdict now travels ON each approved row (same as the analysis side), so the
  // rows need no separate assessment lookup.
  get rows() {
    return self.activeDetections.map((det: any, index: number) => ({
      id: `row-${index}`,
      ...det,
    }));
  },
  get combinedRows() {
    return self.activeDetections.map((det: any, index: number) => ({
      id: `row-${index}`,
      ...det,
    }));
  },
  get detectionColumns(): InspectionColumn[] {
    return [
      {
        field: "distance",
        headerName: translationService.translate("ocrService.inspectionInfo.distance"),
        flex: 1,
        minWidth: 70,
        renderCell: (params) => {
          const timestamp = params.row.timestamp;
          return renderClickablecell(params.value, timestamp);
        },
      },
      {
        field: "timestamp",
        headerName: translationService.translate("ocrService.inspectionInfo.time"),
        flex: 1,
        minWidth: 70,
        renderCell: (params) =>
          renderClickablecell(formatDetectionTime(params.value), params.value),
      },
      {
        field: "text",
        headerName: translationService.translate("ocrService.inspectionInfo.detection"),
        renderCell: (params) => (
          <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: "1.5" }}>
            {params.value}
          </div>
        ),
        flex: 6,
        minWidth: 200,
      },
    ];
  },
  get extractedColumns(): InspectionColumn[] {
    return [
      {
        field: "distance",
        headerName: translationService.translate("ocrService.inspectionInfo.distance"),
        flex: 1,
        minWidth: 70,
        renderCell: (params) => {
          const timestamp = params.row.timestamp;
          return renderClickablecell(params.value, timestamp);
        },
      },
      {
        field: "timestamp",
        headerName: translationService.translate("ocrService.inspectionInfo.time"),
        flex: 1,
        minWidth: 70,
        renderCell: (params) =>
          renderClickablecell(formatDetectionTime(params.value), params.value),
      },
      { field: "main_Code", headerName: "MC", flex: 1 },
      { field: "c1", headerName: "C1", flex: 1 },
      { field: "c2", headerName: "C2", flex: 1 },
      { field: "sa", headerName: "SA", flex: 1 },
      { field: "q1", headerName: "Q1", flex: 1 },
      { field: "q2", headerName: "Q2", flex: 1 },
      { field: "postionFrom", headerName: translationService.translate("ocrService.inspectionInfo.positionFrom"), flex: 1.2 },
      { field: "positionTo", headerName: translationService.translate("ocrService.inspectionInfo.positionTo"), flex: 1.2 },
    ];
  },
  get combinedColumns(): InspectionColumn[] {
    return [
      {
        field: "distance",
        headerName: translationService.translate("ocrService.inspectionInfo.distance"),
        width: 64,
        renderCell: (params) => {
          const timestamp = params.row.timestamp;
          return renderClickablecell(params.value, timestamp);
        },
      },
      {
        field: "timestamp",
        headerName: translationService.translate("ocrService.inspectionInfo.time"),
        width: 64,
        renderCell: (params) =>
          renderClickablecell(formatDetectionTime(params.value), params.value),
      },
      {
        field: "text",
        headerName: translationService.translate("ocrService.inspectionInfo.text"),
        renderCell: (params) => (
          <div style={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: "1.4",
            fontSize: "0.85rem",
            width: "100%",
            paddingTop: 4,
            paddingBottom: 4,
          }}>
            {params.value}
          </div>
        ),
        flex: 4,
        minWidth: 120,
      },
      { field: "main_Code", headerName: "MC", flex: 0.45, minWidth: 36 },
      { field: "c1", headerName: "C1", flex: 0.45, minWidth: 36 },
      { field: "c2", headerName: "C2", flex: 0.45, minWidth: 36 },
      { field: "sa", headerName: "SA", flex: 0.45, minWidth: 36 },
      { field: "q1", headerName: "Q1", flex: 0.45, minWidth: 36 },
      { field: "q2", headerName: "Q2", flex: 0.45, minWidth: 36 },
      { field: "postionFrom", headerName: translationService.translate("ocrService.inspectionInfo.positionFrom"), flex: 0.6, minWidth: 44 },
      { field: "positionTo", headerName: translationService.translate("ocrService.inspectionInfo.positionTo"), flex: 0.6, minWidth: 44 },
    ];
  },
  // NOTE: status / condition / risk are deliberately NOT columns here. The Inspection Graph
  // shows what the inspection found (station, code, text); the reviewer's verdict is shown
  // per observation on the Detection List cards, next to the frame it was judged from.
  get combinedColumnGroup(): InspectionColumnGroup[] {
    return [
      {
        groupId: "position",
        headerName: translationService.translate("ocrService.inspectionInfo.position"),
        children: [{ field: "postionFrom" }, { field: "positionTo" }],
      },
    ];
  },
  get columns(): InspectionColumn[]{
    if(self.view === SelectedView.Detected) return this.detectionColumns;
    if(self.view === SelectedView.Extracted) return this.extractedColumns;
    return this.combinedColumns;
  }
})).actions((self) => ({
  setDetections(data: typeof DetectionModel.Type[]) {
    self.detections.replace(data);
  },
  setView(view: ViewType) {
    self.view = view;
  },
  setSelectedIndex(index: number | null){
    self.selectedIndex = index;
  },
  // The approved detection list as loaded from the review; each row already carries its own
  // status / condition / risk. Passing an empty list is how a file with no review yet falls
  // back to the OCR output.
  setApprovedDetections(data: any[]) {
    self.approvedDetections = data;
  },
  setUseApproved(useApproved: boolean) {
    self.useApproved = useApproved;
    // The two lists can differ in length, so an index into the old one may not exist in the
    // new one â€” clear it rather than highlighting an unrelated row.
    self.selectedIndex = null;
  },
  reset() {
    self.detections.clear();
    self.approvedDetections = [];
    self.view = SelectedView.Detected;
    self.selectedIndex = null;
  }
}));

export const inspectionInfoStorage = InspectionInfoStorageModel.create({
  detections: [],
  view: SelectedView.Detected,
});
